import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';

// Facebook App ID for SDK sharing
const FACEBOOK_APP_ID = '852314097600833';

// Streaming Overlay Section Component
function StreamingOverlaySection({ tournament, instanceDate }) {
  const [selectedTable, setSelectedTable] = useState(1);
  const [copiedOverlay, setCopiedOverlay] = useState(false);
  const [showOverlayOptions, setShowOverlayOptions] = useState(false);

  // Parse custom table numbers from tournament settings
  const getTableNumbers = () => {
    if (tournament.customTableNumbers) {
      const tables = tournament.customTableNumbers.split(',').map(t => parseInt(t.trim(), 10)).filter(t => !isNaN(t));
      return tables.length > 0 ? tables : [1, 2, 3, 4];
    }
    return [1, 2, 3, 4, 5, 6, 7, 8];
  };

  const tableNumbers = getTableNumbers();

  // Generate overlay URL
  const getOverlayUrl = () => {
    if (!tournament?.id) return null;
    let url = `${window.location.origin}/overlay/${tournament.id}/${selectedTable}`;
    // Add instance date if available
    if (instanceDate) {
      // Extract date directly from key to avoid UTC conversion issues
      const simpleDate = instanceDate.split('T')[0];
      url += `?d=${simpleDate}`;
    }
    return url;
  };

  const overlayUrl = getOverlayUrl();

  const handleCopyOverlayUrl = async () => {
    if (!overlayUrl) return;
    try {
      await navigator.clipboard.writeText(overlayUrl);
      setCopiedOverlay(true);
      setTimeout(() => setCopiedOverlay(false), 2000);
    } catch (error) {
      console.error('Failed to copy overlay URL:', error);
    }
  };

  if (!tournament?.id) return null;

  return (
    <div style={{
      marginTop: '25px',
      paddingTop: '20px',
      borderTop: '1px solid var(--border-color)'
    }}>
      <div
        onClick={() => setShowOverlayOptions(!showOverlayOptions)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          padding: '10px 0'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px' }}>📺</span>
          <div>
            <div style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: 'var(--text-primary)'
            }}>
              Streaming Overlay
            </div>
            <div style={{
              fontSize: '12px',
              color: 'var(--text-secondary)'
            }}>
              For OBS, Wirecast, XSplit, vMix
            </div>
          </div>
        </div>
        <span style={{
          fontSize: '18px',
          color: 'var(--text-secondary)',
          transition: 'transform 0.2s',
          transform: showOverlayOptions ? 'rotate(180deg)' : 'rotate(0deg)'
        }}>
          ▼
        </span>
      </div>

      {showOverlayOptions && (
        <div style={{
          backgroundColor: 'var(--bg-tertiary)',
          borderRadius: '8px',
          padding: '15px',
          marginTop: '10px'
        }}>
          {/* Table Selection */}
          <div style={{ marginBottom: '15px' }}>
            <div style={{
              fontSize: '13px',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
              marginBottom: '8px'
            }}>
              Select Table:
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              {tableNumbers.slice(0, 12).map(tableNum => (
                <button
                  key={tableNum}
                  onClick={() => setSelectedTable(tableNum)}
                  style={{
                    padding: '8px 14px',
                    backgroundColor: selectedTable === tableNum ? 'var(--btn-primary)' : 'var(--bg-secondary)',
                    color: selectedTable === tableNum ? '#fff' : 'var(--text-primary)',
                    border: selectedTable === tableNum ? '2px solid var(--btn-primary)' : '2px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Table {tableNum}
                </button>
              ))}
            </div>
          </div>

          {/* Overlay URL */}
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            padding: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '12px'
          }}>
            <div style={{
              flex: 1,
              fontSize: '12px',
              color: 'var(--text-primary)',
              fontFamily: 'monospace',
              wordBreak: 'break-all'
            }}>
              {overlayUrl}
            </div>
            <button
              onClick={handleCopyOverlayUrl}
              style={{
                padding: '6px 12px',
                backgroundColor: copiedOverlay ? 'var(--success)' : 'var(--accent-primary)',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              {copiedOverlay ? '✓ Copied!' : 'Copy'}
            </button>
          </div>

          {/* Instructions */}
          <div style={{
            fontSize: '12px',
            color: 'var(--text-secondary)',
            lineHeight: '1.5'
          }}>
            <strong>How to use:</strong>
            <ol style={{ margin: '8px 0 0 0', paddingLeft: '18px' }}>
              <li>Copy the URL above</li>
              <li>In OBS: Add a Browser Source</li>
              <li>Paste the URL and set width/height (e.g., 400x200)</li>
              <li>Enable "Refresh browser when scene becomes active"</li>
            </ol>
          </div>

          {/* Customization hint */}
          <div style={{
            marginTop: '12px',
            padding: '10px',
            backgroundColor: 'var(--info-bg)',
            border: '1px solid var(--info-border)',
            borderRadius: '6px',
            fontSize: '11px',
            color: 'var(--info-text)'
          }}>
            <strong>Customize with URL parameters:</strong><br />
            <code style={{ fontSize: '10px' }}>?layout=bottom</code> - Bottom bar layout<br />
            <code style={{ fontSize: '10px' }}>?size=large</code> - Larger text (small/medium/large)<br />
            <code style={{ fontSize: '10px' }}>?accent=4CAF50</code> - Custom accent color<br />
            <code style={{ fontSize: '10px' }}>?bg=8</code> - Background opacity (0-9, or "none")
          </div>
        </div>
      )}
    </div>
  );
}

export default function ShareTournamentModal({ isOpen, tournament, onClose, instanceDate = null }) {
  const [copied, setCopied] = useState(false);
  const [shareMode, setShareMode] = useState('instance'); // 'instance' or 'series'
  const [fbSdkLoaded, setFbSdkLoaded] = useState(false);
  const qrCanvasRef = useRef(null);

  // Load Facebook SDK
  useEffect(() => {
    if (window.FB) {
      setFbSdkLoaded(true);
      return;
    }

    // Check if SDK script is already loading
    if (document.getElementById('facebook-jssdk')) {
      return;
    }

    // Load the Facebook SDK
    window.fbAsyncInit = function() {
      window.FB.init({
        appId: FACEBOOK_APP_ID,
        xfbml: true,
        version: 'v18.0'
      });
      setFbSdkLoaded(true);
    };

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  // Generate URL based on share mode
  const getShareUrl = (mode) => {
    if (!tournament?.shareUrl) return null;
    const baseUrl = `${window.location.origin}/tournament/${tournament.shareUrl}`;
    // Instance-specific URL includes the date parameter (simplified to YYYY-MM-DD)
    if (mode === 'instance' && instanceDate) {
      // Extract date directly from key to avoid UTC conversion issues
      const simpleDate = instanceDate.split('T')[0]; // "2026-01-08"
      return `${baseUrl}?d=${simpleDate}`;
    }
    // Series URL goes to the scheduled view (for marketing)
    return baseUrl;
  };

  const shareUrl = getShareUrl(shareMode);

  // Generate QR code when shareUrl changes
  useEffect(() => {
    if (isOpen && shareUrl && qrCanvasRef.current) {
      // Small delay to ensure canvas is mounted
      setTimeout(() => {
        if (qrCanvasRef.current) {
          QRCode.toCanvas(qrCanvasRef.current, shareUrl, {
            width: 200,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#ffffff'
            }
          }, (error) => {
            if (error) console.error('Error generating QR code:', error);
          });
        }
      }, 100);
    }
  }, [isOpen, shareUrl]);

  if (!isOpen || !tournament) return null;

  const handleCopyUrl = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackError) {
        console.error('Failed to copy URL:', fallbackError);
        alert('Failed to copy URL. Please copy manually.');
      }

      document.body.removeChild(textArea);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'var(--bg-modal-overlay)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '12px',
        boxShadow: 'var(--shadow-lg)',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        position: 'relative'
      }}>
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: 'var(--text-secondary)',
            padding: '5px',
            borderRadius: '50%',
            width: '35px',
            height: '35px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Close"
        >
          ×
        </button>

        <div style={{ padding: '40px 30px 30px' }}>
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <h2 style={{
              margin: '0 0 10px 0',
              color: 'var(--text-primary)',
              fontSize: '28px',
              fontWeight: 'bold'
            }}>
              🔗 Share Tournament
            </h2>
            <p style={{
              margin: 0,
              color: 'var(--text-secondary)',
              fontSize: '16px'
            }}>
              Share "{tournament.title}" with others
            </p>
          </div>

          {tournament.isPublic && shareUrl && (
            <div>
              {/* Share Mode Toggle - Only show when there's an instance date */}
              {instanceDate && (
                <div style={{
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: 'var(--info-bg)',
                  borderRadius: '8px',
                  border: '1px solid var(--info-border)'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: 'var(--info-text)',
                    marginBottom: '12px'
                  }}>
                    What do you want to share?
                  </div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      backgroundColor: shareMode === 'instance' ? 'var(--btn-primary)' : 'var(--bg-secondary)',
                      color: shareMode === 'instance' ? '#fff' : 'var(--text-primary)',
                      borderRadius: '6px',
                      border: shareMode === 'instance' ? '2px solid var(--btn-primary)' : '2px solid var(--border-color)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      flex: 1,
                      minWidth: '140px'
                    }}>
                      <input
                        type="radio"
                        name="shareMode"
                        value="instance"
                        checked={shareMode === 'instance'}
                        onChange={() => setShareMode('instance')}
                        style={{ display: 'none' }}
                      />
                      <span style={{ fontSize: '18px' }}>🎯</span>
                      <div>
                        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Today's Bracket</div>
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>For players at the venue</div>
                      </div>
                    </label>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      backgroundColor: shareMode === 'series' ? 'var(--btn-primary)' : 'var(--bg-secondary)',
                      color: shareMode === 'series' ? '#fff' : 'var(--text-primary)',
                      borderRadius: '6px',
                      border: shareMode === 'series' ? '2px solid var(--btn-primary)' : '2px solid var(--border-color)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      flex: 1,
                      minWidth: '140px'
                    }}>
                      <input
                        type="radio"
                        name="shareMode"
                        value="series"
                        checked={shareMode === 'series'}
                        onChange={() => setShareMode('series')}
                        style={{ display: 'none' }}
                      />
                      <span style={{ fontSize: '18px' }}>📅</span>
                      <div>
                        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Tournament Series</div>
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>For social media / marketing</div>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              <div style={{
                backgroundColor: 'var(--bg-tertiary)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '15px'
              }}>
                <div style={{
                  flex: 1,
                  fontSize: '14px',
                  color: 'var(--text-primary)',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all'
                }}>
                  {shareUrl}
                </div>

                <button
                  onClick={handleCopyUrl}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: copied ? 'var(--success)' : 'var(--btn-primary)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {copied ? '✓ Copied!' : '📋 Copy'}
                </button>
              </div>

              {/* QR Code */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginTop: '20px',
                marginBottom: '15px'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: 'var(--text-primary)',
                  marginBottom: '10px'
                }}>
                  📱 Scan QR Code
                </div>
                <canvas
                  ref={qrCanvasRef}
                  style={{
                    border: '2px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '10px',
                    backgroundColor: 'var(--bg-secondary)'
                  }}
                />
                <div style={{
                  fontSize: '12px',
                  color: 'var(--text-secondary)',
                  marginTop: '8px',
                  textAlign: 'center'
                }}>
                  Scan with your phone camera to open the tournament
                </div>
              </div>

              {/* Social Share Buttons */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginTop: '20px',
                marginBottom: '15px',
                paddingTop: '15px',
                borderTop: '1px solid var(--border-color)'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: 'var(--text-primary)',
                  marginBottom: '12px'
                }}>
                  Share on Social Media
                </div>
                <button
                  onClick={() => {
                    // Build detailed tournament info
                    const venueName = tournament.venue?.name || '';
                    const gameType = tournament.rules?.gameType || '';
                    const playerFormat = tournament.rules?.playerFormat || '';
                    const format = tournament.format === 'SINGLE_ELIMINATION' ? 'Single Elimination' :
                                 tournament.format === 'DOUBLE_ELIMINATION' ? 'Double Elimination' :
                                 tournament.format === 'MODIFIED_SINGLE_ELIMINATION' ? 'Modified Single Elimination' :
                                 tournament.format === 'ROUND_ROBIN' ? 'Round Robin' : '';

                    // Format date for instance
                    let dateStr = '';
                    if (shareMode === 'instance' && instanceDate) {
                      const dateObj = new Date(instanceDate);
                      dateStr = dateObj.toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      });
                    } else if (tournament.recurring) {
                      // Show recurring schedule
                      const frequency = tournament.recurring.frequency === 'weekly' ? 'Every week' :
                                      tournament.recurring.frequency === 'biweekly' ? 'Every two weeks' :
                                      'Monthly';
                      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                      const dayName = days[tournament.recurring.dayOfWeek];
                      const time = tournament.recurring.time || '';
                      dateStr = `${frequency} on ${dayName}${time ? ` at ${time}` : ''}`;
                    }

                    // Build comprehensive share text
                    let shareText = `🎱 ${tournament.title}\n`;
                    if (venueName) shareText += `📍 ${venueName}\n`;
                    if (dateStr) shareText += `📅 ${dateStr}\n`;
                    if (gameType) shareText += `🎮 ${gameType}`;
                    if (playerFormat) shareText += ` - ${playerFormat}`;
                    if (gameType || playerFormat) shareText += '\n';
                    if (format) shareText += `🏆 ${format}\n`;
                    shareText += '\nFollow the live bracket:';

                    // Use Facebook SDK if loaded, otherwise fall back to basic sharer
                    if (window.FB && fbSdkLoaded) {
                      window.FB.ui({
                        method: 'share',
                        href: shareUrl,
                        quote: shareText
                      }, function(response) {
                        // Optional callback
                        if (response && !response.error_message) {
                          console.log('Facebook share successful');
                        }
                      });
                    } else {
                      // Fallback to basic sharer URL
                      const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
                      window.open(facebookUrl, '_blank', 'width=600,height=400');
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 24px',
                    backgroundColor: '#1877F2',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#166FE5';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#1877F2';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Share on Facebook
                </button>
              </div>

              <div style={{
                fontSize: '12px',
                color: 'var(--text-secondary)',
                fontStyle: 'italic',
                textAlign: 'center'
              }}>
                💡 Tip: Share this URL with players and spectators to let them follow the tournament progress in real-time!
              </div>

              {/* Streaming Overlay Section */}
              <StreamingOverlaySection tournament={tournament} instanceDate={instanceDate} />
            </div>
          )}

          {!tournament.isPublic && (
            <div style={{
              backgroundColor: 'var(--warning-bg)',
              border: '1px solid var(--warning-border)',
              borderRadius: '6px',
              padding: '15px',
              color: 'var(--warning-text)'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                🔒 Private Tournament
              </div>
              <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                This tournament is currently private. To enable public sharing, edit the tournament from your dashboard and check "Make tournament public" (requires venue and schedule information).
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}